import { makeAutoObservable } from 'mobx';
import i18next from 'i18next';
import { AxiosError } from 'axios';
import type { IUser, VkAuthCode } from '../types/user';
import { usersService } from '../api/services/usersService';
import {
  ADVANCED_MODE_STORAGE_KEY,
  TOKEN_STORAGE_KEY as PLATFORM_TOKEN_STORAGE_KEY,
  USER_STORAGE_KEY as PLATFORM_USER_STORAGE_KEY,
} from '@ai/uimain/src/constants/localStorageKeys';
import {
  TOKEN_STORAGE_KEY as TRAINER_TOKEN_STORAGE_KEY,
  USER_STORAGE_KEY as TRAINER_USER_STORAGE_KEY,
} from '@ai/uitrainer/src/constants/localStorageKeys';
import { authService } from '../api/services/authService';

class UserStore {
  currentUser: IUser | undefined = undefined;
  hasBalance: boolean = false;
  error: string | undefined = undefined;
  token: string | undefined = undefined;
  isSendingLink = false;
  isAdvancedMode: boolean = false;
  TOKEN_STORAGE_KEY: string;
  USER_STORAGE_KEY: string;

  constructor(TOKEN_STORAGE_KEY: string, USER_STORAGE_KEY: string) {
    this.TOKEN_STORAGE_KEY = TOKEN_STORAGE_KEY;
    this.USER_STORAGE_KEY = USER_STORAGE_KEY;
    makeAutoObservable(this, undefined, { autoBind: true });

    this.readFromLocalStorage();
    this.checkHasBalance();

    window.addEventListener('storage', this.handleStorageChange);
  }

  destroy() {
    window.removeEventListener('storage', this.handleStorageChange);
  }

  handleStorageChange = (e: StorageEvent) => {
    // обрабатываем случай разлогина с другой вкладки (когда локал сторадж обновляется, а значения в сторе - нет)
    if (e.key === this.TOKEN_STORAGE_KEY || e.key === this.USER_STORAGE_KEY) {
      if (!e.newValue) {
        this.resetUser();
      }
    }
  };

  checkHasBalance = async () => {
    if (!this.currentUser) return;

    const isActiveRes = await usersService.checkIfActive(this.currentUser.id);
    this.setHasBalance(isActiveRes.data.active);
  };

  checkResponseStatusAndSetError(error: unknown) {
    if (error instanceof AxiosError) {
      if (error.response) {
        switch (error.response.status) {
          case 401:
            this.setError(i18next.t('login.error.wrongEmailOrPassword'));
            break;
          case 403:
            this.setError(i18next.t('login.error.userDisabled'));
            break;
          case 500:
            this.setError(i18next.t('serverIsNotResponding'));
            break;
          case 502:
            this.setError(i18next.t('serverRespond502'));
            break;
        }
      } else {
        this.setError(error.message);
      }
    }
  }

  setIsSendingLink = (v: boolean) => (this.isSendingLink = v);

  readFromLocalStorage() {
    const token = localStorage.getItem(this.TOKEN_STORAGE_KEY);
    if (token) {
      this.updateToken(token);
    }

    const userStr = localStorage.getItem(this.USER_STORAGE_KEY);
    if (userStr) {
      try {
        const user = JSON.parse(userStr);

        if (!user.id) {
          window.location.hash = '#/auth/login';

          return;
        }

        this.updateCurrentUser(user);
      } catch {
        console.error('Неправильная запись UserStore');
      }
    }

    const advancedModeStr = localStorage.getItem(ADVANCED_MODE_STORAGE_KEY);
    if (advancedModeStr) {
      this.updateIsAdvancedMode(advancedModeStr === 'true');
    }
  }

  private async loginFactory(loginMethod: () => Promise<any>) {
    try {
      const tokenRes = await loginMethod();
      this.setToken(tokenRes.data.access_token);
      const userRes = await authService.getCurrent();
      this.updateCurrentUser(userRes.data);
      const isActiveRes = await usersService.checkIfActive(userRes.data.id);
      this.setHasBalance(isActiveRes.data.active);

      return { currentUser: userRes.data, hasBalance: isActiveRes.data.active };
    } catch (error) {
      this.checkResponseStatusAndSetError(error);
      throw error;
    }
  }

  async loginWithCredentials(login: string, password: string) {
    return this.loginFactory(() => authService.login({ username: login, password }));
  }

  async loginWithYandex(access_token: string) {
    return this.loginFactory(() => authService.loginWithYandex({ access_token }));
  }

  async loginWithVk(data: VkAuthCode) {
    return this.loginFactory(() => authService.loginWithVk(data));
  }

  async register(name: string, login: string, password: string, ref: string | null) {
    try {
      const res = await authService.register({ name, login, password, ref });
      return res.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        switch (error.response?.status) {
          case 400:
            this.setError(i18next.t('login.errorRegister'));
            break;

          case 500:
            this.setError(i18next.t('serverIsNotResponding'));
            break;

          default:
            break;
        }
      }

      throw error;
    }
  }

  setCurrentUser(user: IUser | undefined) {
    this.currentUser = user;
  }

  updateCurrentUser(user: IUser | undefined) {
    this.setCurrentUser(user);
    if (user) {
      localStorage.setItem(this.USER_STORAGE_KEY, JSON.stringify(user));
    } else {
      localStorage.removeItem(this.USER_STORAGE_KEY);
    }
  }

  setToken(token: string | undefined) {
    this.token = token;
  }

  updateToken(token: string | undefined) {
    this.setToken(token);
    if (token) {
      localStorage.setItem(this.TOKEN_STORAGE_KEY, token);
    } else {
      localStorage.removeItem(this.TOKEN_STORAGE_KEY);
    }
  }

  setIsAdvancedMode = (v: boolean) => (this.isAdvancedMode = v);

  updateIsAdvancedMode(v: boolean) {
    this.setIsAdvancedMode(v);
    localStorage.setItem(ADVANCED_MODE_STORAGE_KEY, v.toString());
  }

  setError(error: string | undefined) {
    this.error = error;
  }

  setHasBalance(v: boolean) {
    this.hasBalance = v;
  }

  resetUser() {
    this.updateCurrentUser(undefined);
    this.setError(undefined);
    this.updateToken(undefined);
  }

  async verifyEmail(token: string | null) {
    if (!token) {
      this.setError(i18next.t('login.error.noToken'));
      return;
    }

    try {
      const res = await authService.verifyEmail(token);
      this.setError(undefined);
      return res.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        this.setError(`${error.response?.data.detail}`);
      }
    }
  }

  async resetPassword(token: string, password: string) {
    try {
      const res = await authService.resetPassword(token, password);
      this.setError(undefined);
      return res.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        this.setError(`${JSON.stringify(error.response?.data.detail)}`);
      }
    }
  }

  async sendVerificationLink(email: string | null) {
    if (!email) {
      this.setError(i18next.t('login.error.noEmail'));
      return;
    }

    this.setIsSendingLink(true);

    try {
      const res = await authService.sendVerificationLink(email);
      this.setError(undefined);
      return res.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        this.setError(`${error.response?.data.detail}`);
      }
    } finally {
      this.setIsSendingLink(false);
    }
  }

  async sendResetLink(email: string) {
    this.setIsSendingLink(true);

    try {
      const res = await authService.sendResetLink(email);
      this.setError(undefined);
      return res.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        console.log(error);
        this.setError(`${JSON.stringify(error.response?.data.detail) || error.message}`);
      }
    } finally {
      this.setIsSendingLink(false);
    }
  }
}

class CurrentFrontend {
  userStore: UserStore | null = null;
  setUserStore = (v: UserStore | null) => (this.userStore = v);
}

export const currentFrontend = new CurrentFrontend();

const platformUserStore = new UserStore(PLATFORM_TOKEN_STORAGE_KEY, PLATFORM_USER_STORAGE_KEY);

const trainerUserStore = new UserStore(TRAINER_TOKEN_STORAGE_KEY, TRAINER_USER_STORAGE_KEY);

export type FrontendId = 'platform' | 'trainer';

export const getUserStore = (frontendId: FrontendId) => {
  switch (frontendId) {
    case 'platform':
      currentFrontend.setUserStore(platformUserStore);
      return platformUserStore;
    case 'trainer':
      currentFrontend.setUserStore(trainerUserStore);
      return trainerUserStore;
    default:
      currentFrontend.setUserStore(platformUserStore);
      console.error('FRONTEND ID IS NOT DEFINED');
      return platformUserStore;
  }
};
